import React from 'react';
import { SparkIteration } from '../../types/sparkTypes';
import styles from '../../styles/CreativeEditor.module.css';
import { isIterationUnseen, parseTimestamp } from '../../utils/timeUtils';

interface VersionHistoryPanelProps {
  iterations: SparkIteration[];
  activeIterationFqn: string | null;
  displayedIterationFqn: string | null;
  isReadOnly: boolean;
  isDeletingIteration: boolean;
  locallySeenAt: number | null;
  onClose: () => void;
  onDisplayIteration: (fqn: string) => void;
  onSetActiveIteration: (fqn: string) => Promise<void>;
  onCreateIteration: (fqn: string) => Promise<void>;
  onDeleteIteration: (fqn: string) => void;
}

const VersionHistoryPanel: React.FC<VersionHistoryPanelProps> = ({
  iterations,
  activeIterationFqn,
  displayedIterationFqn,
  isReadOnly,
  isDeletingIteration,
  locallySeenAt,
  onClose,
  onDisplayIteration,
  onSetActiveIteration,
  onCreateIteration,
  onDeleteIteration,
}) => {
  return (
    <>
      <div className={styles.versionHistoryBackdrop} onClick={onClose} />
      <div className={styles.versionHistoryPanel}>
        <div className={styles.versionHistoryHeader}>
          <h3 className={styles.versionHistoryTitle}>Version history</h3>
          <button className={styles.versionHistoryClose} onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>
        <div className={styles.versionHistoryList}>
          {iterations.map((iter) => {
            const isActive = activeIterationFqn === iter.fqn;
            const isDisplayed = displayedIterationFqn === iter.fqn;
            const canDelete = !isActive && iterations.length > 1;

            // Compute unseen status
            const baseUnseen = isIterationUnseen(iter);
            const unseen = isDisplayed
              ? (() => {
                  const lastSeenBase = parseTimestamp(iter._last_time_seen || null);
                  const effectiveLastSeen = Math.max(lastSeenBase, locallySeenAt ?? 0);
                  const changedAt = parseTimestamp(iter._changed_at || null);
                  if (!iter._last_time_seen && locallySeenAt == null) return true;
                  return changedAt > effectiveLastSeen;
                })()
              : baseUnseen;

            return (
              <button
                key={iter.fqn}
                className={`${styles.versionHistoryItem} ${isDisplayed ? styles.versionHistoryItemActive : ''}`}
                onClick={() => onDisplayIteration(iter.fqn)}
              >
                {unseen && <span className={styles.versionUnseenDot} />}
                <span className={styles.versionHistoryItemLabel}>Version {iter._iteration_number}</span>
                <div className={styles.versionItemActions}>
                  {isActive && (
                    <span className={styles.versionActiveIndicator}>Active</span>
                  )}
                  {!isActive && !isReadOnly && (
                    <button
                      className={`${styles.versionActionButton} ${styles.versionSetActive}`}
                      onClick={(e) => { e.stopPropagation(); onSetActiveIteration(iter.fqn); }}
                      title={`Set v${iter._iteration_number} as active`}
                    >
                      Set Active
                    </button>
                  )}
                  {!isReadOnly && (
                    <button
                      className={`${styles.versionActionButton} ${styles.versionNew}`}
                      onClick={(e) => { e.stopPropagation(); onCreateIteration(iter.fqn); }}
                      title={`Duplicate v${iter._iteration_number}`}
                    >
                      Duplicate
                    </button>
                  )}
                  {!isReadOnly && canDelete && (
                    <button
                      className={`${styles.versionActionButton} ${styles.versionDelete}`}
                      onClick={(e) => { e.stopPropagation(); onDeleteIteration(iter.fqn); }}
                      title={`Delete v${iter._iteration_number}`}
                      disabled={isDeletingIteration}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default VersionHistoryPanel;
